import { seatingChartKey } from '../../../lib/groupQueryKeys';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { seatingApi } from '../../../services/seating-api-client';
import { seatingErrorCode, seatingErrorMessage } from '../../../lib/seatingErrors';
import { occupantName, seatRequestForAssignment, seatingProgress } from '../../../lib/seatingCandidates';
import { placementOutcome } from '../../../lib/seatingPlacement';
import { DEFAULT_SEAT_PITCH_CM, computeTableGeometry, seatLabel } from '../../../lib/seatingGeometry';
import { formatInZone } from '../../../lib/tripTime';
import AddTableDialog from './AddTableDialog';
import AutoSeatDialog from './AutoSeatDialog';
import EditTableDialog from './EditTableDialog';
import SeatOccupantDialog from './SeatOccupantDialog';
import SeatPickerDialog from './SeatPickerDialog';
import SeatingProgressPanel from './SeatingProgress';
import TablePlan from './TablePlan';
import type {
  AssignSeatRequest,
  CreateSeatingTableRequest,
  SeatAssignment,
  SeatingChartDetail,
  SeatingTable,
  UpdateSeatingTableRequest,
} from '../../../types/seating';
import { plural } from '../../../lib/plural';

/** Wider than this and a table wants the whole row — a 7.2m banquet is 6:1. */
const FULL_WIDTH_ASPECT = 2.2;

/** The seat the host is looking at, and what the next tap will do with it. */
interface SeatRef { table: SeatingTable; seatIndex: number }
interface ArmedPlacement { body: AssignSeatRequest; name: string; from?: SeatRef }
/** A tap the API would refuse, held on screen with the one move that clears it. */
interface BlockedPlacement extends SeatRef { assignment: SeatAssignment; reason: string }

/**
 * One chart: the room, who is in it, and who is still standing.
 *
 * There is no canvas and nothing is dragged. Tables flow in a grid by display
 * order; seating is tap a chair, pick a guest, or pick a guest and tap a chair.
 * Both paths reach the same PUT, which is also how a MOVE works — assigning a
 * guest who is already seated vacates their old chair in the same transaction,
 * so there is no moment where they are nowhere.
 *
 * Every write carries If-Match. Two hosts genuinely do arrange the same room at
 * the same time the night before, and last-write-wins throws away an hour of
 * someone's work without saying so.
 */
export default function SeatingChartEditor({
  groupId,
  chartId,
  timeZone,
  onChanged,
  onBack,
}: {
  groupId: string;
  chartId: string;
  /** The trip's zone. A dinner time shown in the host's own zone is a missed dinner. */
  timeZone: string;
  /** The chart list shows counts, so it is stale after every seat change. */
  onChanged: () => void;
  onBack: () => void;
}) {
  const chartQuery = useQuery<SeatingChartDetail>(
    seatingChartKey(groupId, chartId),
    () => seatingApi.getChart(groupId, chartId),
  );

  const [placement, setPlacement] = useState<ArmedPlacement | null>(null);
  const [picking, setPicking] = useState<SeatRef | null>(null);
  const [occupied, setOccupied] = useState<(SeatRef & { assignment: SeatAssignment }) | null>(null);
  const [blocked, setBlocked] = useState<BlockedPlacement | null>(null);
  const [addingTable, setAddingTable] = useState(false);
  const [editingTable, setEditingTable] = useState<SeatingTable | null>(null);
  const [autoSeating, setAutoSeating] = useState(false);

  const detail = chartQuery.data;
  const tables = detail?.tables ?? [];
  const candidates = detail?.candidates ?? [];
  const pitch = detail?.chart.seatPitchCm ?? DEFAULT_SEAT_PITCH_CM;
  const sourceVersion = detail?.chart.sourceVersion;

  const settled = () => {
    chartQuery.refetch();
    onChanged();
  };
  const closeAll = () => {
    setPicking(null);
    setOccupied(null);
    setPlacement(null);
    setBlocked(null);
  };

  const assignMutation = useMutation(
    (vars: {
      tableId: string;
      seatIndex: number;
      body: AssignSeatRequest;
      swap?: boolean;
      allowNonAttendee?: boolean;
    }) =>
      seatingApi.assignSeat(groupId, chartId, vars.tableId, vars.seatIndex, vars.body, {
        sourceVersion,
        swap: vars.swap,
        allowNonAttendee: vars.allowNonAttendee,
      }),
    {
      onSuccess: () => { closeAll(); settled(); },
      // Get out of the dialog's way on failure: every recoverable refusal —
      // occupied chair, no claim, stale version — is answered by a button on the
      // page-level alert, and a modal sitting on top of it hides the fix.
      onError: () => { setPicking(null); setOccupied(null); },
    },
  );
  const unassignMutation = useMutation(
    (vars: { tableId: string; seatIndex: number }) =>
      seatingApi.unassignSeat(groupId, chartId, vars.tableId, vars.seatIndex, sourceVersion),
    { onSuccess: () => { closeAll(); settled(); }, onError: () => setOccupied(null) },
  );
  const tablesMutation = useMutation(
    (bodies: CreateSeatingTableRequest[]) => seatingApi.createTables(groupId, chartId, bodies),
    { onSuccess: () => { setAddingTable(false); settled(); } },
  );
  const editTableMutation = useMutation(
    (vars: { tableId: string; body: UpdateSeatingTableRequest; reflow?: boolean }) =>
      seatingApi.updateTable(groupId, chartId, vars.tableId, vars.body, {
        sourceVersion,
        reflow: vars.reflow,
      }),
    {
      onSuccess: () => { setEditingTable(null); settled(); },
      // Same reason as the seat writes: the one refusal this can produce —
      // "you would strand these people" — is answered by a button on the
      // page-level alert, and a modal on top of it hides the fix.
      onError: () => setEditingTable(null),
    },
  );
  const deleteTableMutation = useMutation(
    (vars: { tableId: string; force?: boolean }) =>
      seatingApi.deleteTable(groupId, chartId, vars.tableId, vars.force),
    { onSuccess: settled },
  );
  const clearReleasedMutation = useMutation(
    () => seatingApi.clearReleased(groupId, chartId),
    { onSuccess: settled },
  );
  const publishMutation = useMutation(
    () => seatingApi.updateChart(groupId, chartId, { status: 'published' }, sourceVersion),
    { onSuccess: settled },
  );
  /*
    Back to draft, which takes the room off every guest's page again.

    'draft' is host-settable by the same PATCH — only 'archived' is reserved to
    DELETE. Publishing is now the switch that shows guests their own seat, and a
    switch a host cannot flip back is one they are right to be afraid of.
  */
  const unpublishMutation = useMutation(
    () => seatingApi.updateChart(groupId, chartId, { status: 'draft' }, sourceVersion),
    { onSuccess: settled },
  );

  const saving =
    assignMutation.isLoading || unassignMutation.isLoading || deleteTableMutation.isLoading
    || editTableMutation.isLoading || clearReleasedMutation.isLoading || publishMutation.isLoading
    || unpublishMutation.isLoading;
  const failure = [assignMutation, unassignMutation, deleteTableMutation, editTableMutation, clearReleasedMutation, publishMutation, unpublishMutation]
    .map((mutation) => mutation.error)
    .find(Boolean);
  const error = seatingErrorMessage(failure);
  const code = seatingErrorCode(failure);
  // The write a recovery button re-fires, carrying the flag the host just agreed
  // to (swap, seat-anyway, force).
  const lastAssign = assignMutation.variables;
  const lastDelete = deleteTableMutation.variables;
  const lastEdit = editTableMutation.variables;
  // Who the API said the shrink would strand. It names them rather than
  // counting them, because "move Ana and Bruno" is a job and "move 2 people" is
  // a puzzle.
  const strandedBy = (
    (failure as { response?: { data?: { occupied?: { displayName?: string }[] } } } | undefined)
      ?.response?.data?.occupied ?? []
  ).map((entry) => entry.displayName).filter(Boolean);

  const progress = useMemo(() => seatingProgress(candidates, tables), [candidates, tables]);
  const stale = detail?.staleAssignments ?? [];

  const seatNameFor = (ref: SeatRef) =>
    `${ref.table.label || 'Table'} · ${seatLabel(computeTableGeometry(ref.table, pitch), ref.seatIndex)}`;

  const seat = (ref: SeatRef, body: AssignSeatRequest, swap = false) => {
    assignMutation.reset();
    assignMutation.mutate({ tableId: ref.table.id, seatIndex: ref.seatIndex, body, swap });
  };

  const handleSeatClick = (table: SeatingTable, seatIndex: number, assignment: SeatAssignment | null) => {
    const ref = { table, seatIndex };
    if (placement) {
      setBlocked(null);
      const outcome = placementOutcome(
        {
          body: placement.body,
          name: placement.name,
          from: placement.from
            ? { tableId: placement.from.table.id, seatIndex: placement.from.seatIndex }
            : undefined,
        },
        { tableId: table.id, seatIndex },
        assignment,
      );
      switch (outcome.kind) {
        case 'cancel':
          setPlacement(null);
          return;
        // Landing on an occupied chair is a swap, not a refusal — but it moves
        // someone the host did not name, so it is asked for out loud.
        case 'swap':
          if (!window.confirm(outcome.prompt)) return;
          seat(ref, placement.body, true);
          return;
        // No chair to trade, so ?swap=true would 409 with nothing behind it.
        // Say what will work instead of sending the tap into the refusal.
        case 'refuse':
          if (assignment) setBlocked({ ...ref, assignment, reason: outcome.reason });
          return;
        default:
          seat(ref, placement.body);
          return;
      }
    }
    if (assignment) setOccupied({ ...ref, assignment });
    else setPicking(ref);
  };

  if (chartQuery.isLoading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={20} /><Typography color="text.secondary">Loading the room…</Typography>
      </Stack>
    );
  }
  if (chartQuery.error || !detail) {
    return <Alert severity="error">Could not load this seating chart.</Alert>;
  }

  const dinner = detail.dinner;
  // Publishing over released guests is allowed and warned, never blocked — the
  // host may well know someone is still coming. `stale_assignments:N`.
  const publishedStale = Number(
    (publishMutation.data?.warnings ?? [])
      .find((warning) => warning.startsWith('stale_assignments:'))
      ?.split(':')[1] ?? 0,
  );
  const resetAll = () => {
    assignMutation.reset();
    unassignMutation.reset();
    deleteTableMutation.reset();
    editTableMutation.reset();
    clearReleasedMutation.reset();
    publishMutation.reset();
    unpublishMutation.reset();
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={800}>{detail.chart.name || 'Seating chart'}</Typography>
              <Chip
                size="small"
                label={detail.chart.status === 'published' ? 'Published' : 'Draft'}
                color={detail.chart.status === 'published' ? 'success' : 'default'}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {dinner
                ? `${dinner.venueName}${dinner.reservedAt ? ` · ${formatInZone(dinner.reservedAt, dinner.timeZone || timeZone)}` : ''}`
                : 'Not tied to a dinner — everyone on the trip who has not declined can be seated.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={onBack}>All charts</Button>
            <Button variant="outlined" onClick={() => setAddingTable(true)}>Add a table</Button>
            <Button
              variant="outlined"
              disabled={saving || tables.length === 0}
              onClick={() => { closeAll(); setAutoSeating(true); }}
            >
              Auto-seat
            </Button>
            {detail.chart.status === 'draft' ? (
              <Button
                variant="contained"
                disabled={saving || tables.length === 0}
                onClick={() => {
                  // Publishing now reaches people. Asked out loud, once, rather
                  // than left to a caption a host may not have read.
                  if (!window.confirm(
                    'Publish this room? Every guest seated on it will see their own table and seat number on their trip page.',
                  )) return;
                  publishMutation.mutate();
                }}
              >
                {publishMutation.isLoading ? 'Publishing…' : 'Publish'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={() => unpublishMutation.mutate()}
              >
                {unpublishMutation.isLoading ? 'Unpublishing…' : 'Unpublish'}
              </Button>
            )}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {/*
            Says what publishing DOES today.

            This copy has now been wrong in both directions. It first promised a
            guest view that no client consumed; it was corrected to say guests
            could see nothing, and that became untrue the moment the guest page
            started calling the endpoint. The rule it keeps failing is the same
            one: describe the system as deployed, and change this line in the
            commit that changes the system.

            Both states carry the guest fact, because the host decides whether
            to publish while looking at the DRAFT caption. Telling them only
            afterwards leaves the button "Publish" to imply, unopposed, that it
            reaches nobody.

            What a guest sees is their OWN table and seat number, and the names
            at that table — never the room, never anyone else's placement. That
            distinction is the whole reason publishing is safe to offer.
          */}
          {detail.chart.status === 'published'
            ? 'Published. Everyone seated here can see their own table and seat number on their trip page, and who else is at their table — never the rest of the room. Unpublish takes it back off.'
            : 'Draft. Rearrange freely — nobody but the hosts can see a draft. Publishing shows each guest their own seat.'}
        </Typography>
      </Paper>

      {error ? (
        <Alert
          severity={code === 'version_conflict' ? 'warning' : 'error'}
          action={
            code === 'version_conflict' ? (
              <Button size="small" onClick={() => { resetAll(); chartQuery.refetch(); }}>Reload</Button>
            ) : code === 'not_attending' && lastAssign ? (
              <Button
                size="small"
                onClick={() => assignMutation.mutate({ ...lastAssign, allowNonAttendee: true })}
              >
                Seat them anyway
              </Button>
            ) : code === 'seat_occupied' && lastAssign ? (
              <Button size="small" onClick={() => assignMutation.mutate({ ...lastAssign, swap: true })}>
                Swap them
              </Button>
            ) : code === 'seat_index_out_of_range' && lastEdit ? (
              // The one edit that can strand someone. Reflow is offered, never
              // taken automatically: it moves guests the host did not name, and
              // the place cards may already be printed.
              <Button
                size="small"
                onClick={() => {
                  const who = strandedBy.length ? strandedBy.join(', ') : 'the guests past the new end';
                  if (!window.confirm(
                    `Shuffle ${who} up into the chairs that remain? Everyone at this table may end up in a different seat than the one on their place card.`,
                  )) return;
                  editTableMutation.mutate({ ...lastEdit, reflow: true });
                }}
              >
                Shuffle them up
              </Button>
            ) : code === 'table_has_assignments' && lastDelete ? (
              <Button
                size="small"
                color="error"
                onClick={() => deleteTableMutation.mutate({ ...lastDelete, force: true })}
              >
                Delete and unseat them
              </Button>
            ) : null
          }
        >
          {error}
          {code === 'seat_index_out_of_range' && strandedBy.length ? (
            <> Sitting past the new end: {strandedBy.join(', ')}.</>
          ) : null}
        </Alert>
      ) : null}

      {blocked ? (
        <Alert
          severity="warning"
          action={
            // While a placement is armed every chair tap belongs to it, so this
            // is the host's only way into the chair that is in the way.
            <Button
              size="small"
              onClick={() => {
                setOccupied({ table: blocked.table, seatIndex: blocked.seatIndex, assignment: blocked.assignment });
                setBlocked(null);
              }}
            >
              Open that chair
            </Button>
          }
        >
          {blocked.reason}
        </Alert>
      ) : null}

      {/*
        Reflow moved people. Saying who, and to which chair, is the difference
        between a host reprinting two place cards and reprinting fourteen.
      */}
      {(editTableMutation.data?.reflowed ?? []).length > 0 ? (
        <Alert severity="info" onClose={() => editTableMutation.reset()}>
          Shuffled up:{' '}
          {(editTableMutation.data?.reflowed ?? [])
            .map((move) => `${move.displayName || 'someone'} → seat ${move.toSeatIndex + 1}`)
            .join(', ')}
          . Check any place cards you have already printed.
        </Alert>
      ) : null}

      {publishedStale > 0 ? (
        <Alert severity="info">
          Published with {publishedStale} guest{publishedStale === 1 ? '' : 's'} seated who no longer
          hold a spot. That is allowed — you know who is actually coming.
        </Alert>
      ) : null}

      {stale.length > 0 ? (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              disabled={saving}
              onClick={() => {
                if (!window.confirm(`Take all ${stale.length} of them out of their chairs?`)) return;
                clearReleasedMutation.mutate();
              }}
            >
              Clear them
            </Button>
          }
        >
          {stale.length} seated {stale.length === 1 ? 'guest no longer holds' : 'guests no longer hold'} a
          spot at this dinner — {stale.slice(0, 3).map((entry) => `${entry.displayName || 'someone'} at ${entry.tableLabel}`).join(', ')}
          {stale.length > 3 ? `, and ${stale.length - 3} more` : ''}. Their chairs are kept until you say
          otherwise.
        </Alert>
      ) : null}

      <SeatingProgressPanel
        progress={progress}
        placing={placement?.name}
        onPlace={(body, name) => setPlacement({ body, name })}
        onCancelPlacing={() => { setPlacement(null); setBlocked(null); }}
      />

      {tables.length === 0 ? (
        <Alert severity="info">
          No tables yet. Add the room's tables — a round of 10, a square, or one long banquet table —
          and the chairs appear ready to fill.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' },
          }}
        >
          {tables.map((table) => {
            const extent = computeTableGeometry(table, pitch).extent;
            // A long table drawn in a narrow grid cell is a row of dots. Give it
            // the whole row — it is the shape the operator asked for by name.
            const wide = extent.width / Math.max(extent.height, 1) >= FULL_WIDTH_ASPECT;
            return (
              <Box key={table.id} sx={{ gridColumn: wide ? '1 / -1' : 'auto' }}>
                <TablePlan
                  table={table}
                  seatPitchCm={pitch}
                  placing={placement?.name}
                  onSeatClick={(seatIndex, assignment) => handleSeatClick(table, seatIndex, assignment)}
                  onEdit={() => { editTableMutation.reset(); setEditingTable(table); }}
                  onDelete={() => {
                    const occupiedCount = (table.seats ?? []).filter((entry) => entry.assignment).length;
                    const message = occupiedCount
                      ? `${table.label} has ${plural(occupiedCount, 'person', 'people')} in it. Delete it and unseat them?`
                      : `Delete ${table.label}?`;
                    if (!window.confirm(message)) return;
                    deleteTableMutation.mutate({ tableId: table.id, force: occupiedCount > 0 });
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {picking ? (
        <SeatPickerDialog
          seatName={seatNameFor(picking)}
          candidates={candidates}
          tables={tables}
          saving={assignMutation.isLoading}
          onClose={() => { assignMutation.reset(); setPicking(null); }}
          onSeat={(body) => seat(picking, body)}
        />
      ) : null}

      {occupied ? (
        <SeatOccupantDialog
          seatName={seatNameFor(occupied)}
          assignment={occupied.assignment}
          saving={saving}
          onClose={() => { unassignMutation.reset(); setOccupied(null); }}
          onMove={() => {
            // Arm the move and get out of the way: the next chair tap lands it.
            setPlacement({
              body: seatRequestForAssignment(occupied.assignment),
              name: occupantName(occupied.assignment),
              from: occupied,
            });
            setOccupied(null);
          }}
          onUnseat={() =>
            unassignMutation.mutate({ tableId: occupied.table.id, seatIndex: occupied.seatIndex })
          }
        />
      ) : null}

      {editingTable ? (
        <EditTableDialog
          table={editingTable}
          // Its own name is not a collision with itself; every other one is.
          otherLabels={tables.filter((entry) => entry.id !== editingTable.id).map((entry) => entry.label)}
          tableCount={tables.length}
          seatPitchCm={pitch}
          saving={editTableMutation.isLoading}
          error={seatingErrorMessage(editTableMutation.error)}
          onClose={() => { editTableMutation.reset(); setEditingTable(null); }}
          onSave={(body) => editTableMutation.mutate({ tableId: editingTable.id, body })}
        />
      ) : null}

      {autoSeating ? (
        <AutoSeatDialog
          groupId={groupId}
          chartId={chartId}
          onClose={() => setAutoSeating(false)}
          onApplied={() => { setAutoSeating(false); settled(); }}
        />
      ) : null}

      {addingTable ? (
        <AddTableDialog
          existingLabels={tables.map((table) => table.label)}
          seatPitchCm={pitch}
          saving={tablesMutation.isLoading}
          error={seatingErrorMessage(tablesMutation.error)}
          onClose={() => { tablesMutation.reset(); setAddingTable(false); }}
          onAdd={(bodies) => tablesMutation.mutate(bodies)}
        />
      ) : null}
    </Stack>
  );
}
